/**
 * Language registry + startup-language resolution. Kept separate from the
 * i18next instance (`./index.ts`) so UI (the Account selector) can import the
 * option list without pulling in i18next.
 */

/** Supported UI languages. `label` is the native name shown in the selector. */
export const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'zh-TW', label: '繁體中文' },
  { value: 'ja', label: '日本語' },
] as const;

export type Language = (typeof LANGUAGES)[number]['value'];

export const DEFAULT_LANGUAGE: Language = 'en';

/** Mirrors the `khorium-theme` convention used for the theme preference. */
export const LANGUAGE_STORAGE_KEY = 'khorium-lang';

export function isLanguage(
  value: string | null | undefined,
): value is Language {
  return LANGUAGES.some((l) => l.value === value);
}

/**
 * Best-effort map from a browser locale (`navigator.language`) to a supported
 * language. Only the language/script prefix matters: any `ja*` → Japanese, and
 * Traditional-Chinese variants (`zh-Hant`, `zh-TW`, `zh-HK`, `zh-MO`) → zh-TW.
 * Simplified Chinese has no bundle yet, so it falls through to the default.
 */
function matchBrowserLanguage(locale: string): Language | undefined {
  const lower = locale.toLowerCase();
  if (lower.startsWith('ja')) return 'ja';
  if (
    lower.startsWith('zh-hant') ||
    lower.startsWith('zh-tw') ||
    lower.startsWith('zh-hk') ||
    lower.startsWith('zh-mo')
  ) {
    return 'zh-TW';
  }
  return undefined;
}

/** Stored preference → browser language → default. */
export function resolveInitialLanguage(): Language {
  // Guard browser globals so importing i18n is safe in node-env tests / SSR.
  if (typeof localStorage !== 'undefined') {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (isLanguage(stored)) return stored;
  }

  if (typeof navigator !== 'undefined') {
    for (const locale of navigator.languages ?? [navigator.language]) {
      const matched = matchBrowserLanguage(locale);
      if (matched) return matched;
    }
  }
  return DEFAULT_LANGUAGE;
}
